import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftRight,
  Beaker,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  History,
  MapPin,
  MoreHorizontal,
  Package,
  Printer,
  Search,
  Send,
  Tags,
  Truck,
} from 'lucide-react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortRows, type SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import type { CreatedProduct } from '@/types/products'
import { groupProductsByLineage } from '@/lib/productTree'
import { associatedFormLabel, productPatientLink } from '@/lib/productAssociatedForm'
import {
  PRODUCT_SCOPE_LABEL,
  productScopeEmptyMessage,
  productScopes,
  type ProductScope,
} from '@/lib/productDialogScopes'
import { filterProductsForShipping, isShippable, productLocation } from '@/lib/shipping'
import { useLocationText } from '@/hooks/useStoragePlaces'
import { cn } from '@/lib/utils'
import { hasLimsResults } from '@/lib/limsResults'
import { isSuperseded, supersessionLinks } from '@/lib/productSupersession'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { ChangeProductCodeDialog } from '@/components/procedure/ChangeProductCodeDialog'
import { LocationLines } from '@/components/inventory/storage/picker/LocationTrail'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { TruckFastIcon } from '@/components/shipping/TruckFastIcon'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'

/**
 * Per-user column layout for the on-form product list.
 *
 * Its own `tableId`, deliberately NOT the Products page's `products-created`:
 * the two tables answer different questions (everything this tenant has made
 * vs what this one form produced) and carry different columns, so sharing a
 * saved layout would let a change on one silently reshape the other.
 *
 * `-v2` since the columns were regrouped (Product = DIN + code, Source = form +
 * patient): a layout saved against the old eight columns would hide or size
 * columns that no longer exist, so it starts fresh rather than half-applying.
 *
 * Product is locked because it identifies the row, and Actions because hiding
 * it would leave a manage dialog with nothing to manage.
 */
const FORM_PRODUCTS_TABLE_ID = 'products-on-form-v2'

const FORM_PRODUCTS_COLUMNS: TableColumnDef[] = [
  { id: 'product', header: 'Product', locked: true, sortable: true },
  // Which form made it, and for whom: under "All forms" and "This patient" the
  // list spans forms and procedures, and this is what tells the rows apart.
  { id: 'source', header: 'Source', sortable: true },
  { id: 'volume', header: 'Volume', sortable: true },
  { id: 'storageLocation', header: 'Location', sortable: true },
  { id: 'created', header: 'Created', sortable: true },
  { id: 'actions', header: '', locked: true },
]

/**
 * Column sorting (z8nrz7e4h6). The list is a split tree, so the sort orders the
 * top-level products and, separately, each parent's aliquots under it — a
 * child never leaves its parent. "Not placed" is an absence and sorts last.
 */
const FORM_PRODUCTS_SORT: SortAccessors<CreatedProduct> = {
  product: p => p.din,
  source: p => associatedFormLabel(p) ?? p.patientName ?? p.patientMrn,
  volume: p => p.volumeMl,
  storageLocation: p => productLocation(p).text || null,
  created: p => p.createdAt,
}

/**
 * Manage Products — the panel behind the count on the "P" icon.
 *
 * Read-and-act on the products of this form, without leaving the procedure:
 * reprint a label exactly as it printed the first time, re-open Label Printing
 * to print a different label, or place/move the product in storage. Split
 * children appear nested under their parent, told apart by the division
 * suffix on their product code.
 *
 * Three tabs, see `productDialogScopes`: All forms (the procedure, the one it
 * opens on), This form and This patient (all their procedures). There is no
 * tenant-wide tab (I-20).
 *
 * Laid out to be read at a glance: one Product column (DIN over product code),
 * one Source column (form over patient), the location as the slot over the
 * room it is in, and the two everyday actions — place/move and reprint — on the
 * row, with the rest named in a ⋯ menu rather than as eight bare icons.
 *
 * Editing a product's own fields (volume and its reason/audit trail) is
 * deliberately not here — that is z8nrz7d4y6, and the backend has no product
 * update endpoint yet.
 *
 * Change product code (z8nrz7e72t) is: it re-codes a bag by superseding it with
 * a new product, so both rows stay in this list — the old one greyed with what
 * replaced it, the new one badged "changed". A superseded row offers only its
 * audit trail; every other action belongs to its replacement.
 */
export function ManageProductsDialog({
  open,
  onClose,
  products,
  isLoading = false,
  onReprint,
  onChangeLabel,
  onRelocate,
  onUpdateVolume,
  onViewHistory,
  onViewLims,
  onShip,
  onSendToEpic,
  procedureProducts,
  procedureProductsLoading = false,
  patientProducts,
  patientProductsLoading = false,
  patientMrn,
  title,
  description,
  onOpenLabels,
  pendingLabelCount = 0,
}: {
  open: boolean
  onClose: () => void
  products: CreatedProduct[]
  isLoading?: boolean
  /** Every product of the current procedure; when given, an "All forms" tab appears. */
  procedureProducts?: CreatedProduct[]
  procedureProductsLoading?: boolean
  /** Every product of this patient, across procedures; when given, a "This patient" tab appears. */
  patientProducts?: CreatedProduct[]
  patientProductsLoading?: boolean
  /** Whose run this is, shown at the top after the title (I-20); omitted for a run with no patient. */
  patientMrn?: string | null
  /** Defaults to the on-form wording; the Overall Status board lists a whole procedure. */
  title?: string
  description?: string
  onReprint: (product: CreatedProduct) => void
  onChangeLabel: (product: CreatedProduct) => void
  onRelocate: (product: CreatedProduct) => void
  onUpdateVolume: (product: CreatedProduct) => void
  onViewHistory: (product: CreatedProduct) => void
  /** Open the read-only LIMS results for one product. */
  onViewLims: (product: CreatedProduct) => void
  /**
   * Release this product's results, or send its PDF, to Epic (z8nrz7ffzv /
   * z8nrz7fgcp). Omitted where the caller does not offer it, which hides it.
   */
  onSendToEpic?: (product: CreatedProduct) => void
  /**
   * Ship one product to a partner (z8nrz7eaex). Omitted where the caller does
   * not offer shipping (e.g. no products.ship), which hides the action.
   */
  onShip?: (product: CreatedProduct) => void
  /** Open this form's printed labels (z8nrz7dt48); omitted where there is no form to scope them to. */
  onOpenLabels?: () => void
  /** Labels on this form still waiting for verification, shown on the button. */
  pendingLabelCount?: number
}) {
  const cols = useTableColumns(FORM_PRODUCTS_TABLE_ID, FORM_PRODUCTS_COLUMNS)
  const { can } = usePermission()
  const canChangeCode = can(PERMISSIONS.productsManage)
  const [changing, setChanging] = useState<CreatedProduct | null>(null)
  // The row menus portal into the dialog itself: out at the body they would be
  // unclickable under the modal, and rendered in place a row below paints over
  // them (see `PopoverContent`).
  const [dialogEl, setDialogEl] = useState<HTMLDivElement | null>(null)
  // Split parents start collapsed: the list answers "what did this form
  // make", and a four-way split would otherwise bury the other products.
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  // Sorted before grouping: grouping keeps the incoming order of the top-level
  // products, and the children are then ordered within their own group.
  // Search by DIN, product code or location (the full address of a stored
  // product, or the partner code of a shipped one) — as the Product page does.
  const [q, setQ] = useState('')
  const scopes = productScopes({
    procedure: procedureProducts !== undefined,
    patient: patientProducts !== undefined,
  })
  const [scope, setScope] = useState<ProductScope>(scopes[0] ?? 'form')
  // Each opening starts clean: no leftover search, and on the first tab — All
  // forms where there is one, so the list is never empty just because this
  // form made nothing itself.
  useEffect(() => {
    if (!open) return
    setQ('')
    setScope(scopes[0] ?? 'form')
  }, [open])
  const lists: Record<ProductScope, { rows: CreatedProduct[]; loading: boolean }> = {
    form: { rows: products, loading: isLoading },
    procedure: { rows: procedureProducts ?? [], loading: procedureProductsLoading },
    patient: { rows: patientProducts ?? [], loading: patientProductsLoading },
  }
  const shown = lists[scope].rows
  const loading = lists[scope].loading
  const locate = useLocationText()
  const matching = useMemo(() => filterProductsForShipping(shown, q, locate), [shown, q, locate])
  const sortedProducts = useSortedRows(matching, cols.sort, FORM_PRODUCTS_SORT)
  const groups = groupProductsByLineage(sortedProducts).map(g =>
    cols.sort ? { ...g, children: sortRows(g.children, cols.sort, FORM_PRODUCTS_SORT) } : g,
  )
  // From the whole list, not the search result: a search for the new code
  // should still say what it was changed from.
  const links = useMemo(() => supersessionLinks(shown), [shown])
  const tally = useMemo(() => {
    const out = { stored: 0, unplaced: 0, shipped: 0 }
    for (const p of shown) {
      const kind = productLocation(p).kind
      if (kind === 'stored') out.stored++
      else if (kind === 'shipped') out.shipped++
      else out.unplaced++
    }
    return out
  }, [shown])
  const rowProps = (p: CreatedProduct) => ({
    replacedBy: links.replacedBy.get(p.id),
    changedFrom: links.changedFrom.get(p.id),
    onChangeCode: canChangeCode ? setChanging : undefined,
    menuContainer: dialogEl,
  })

  function toggle(id: string) {
    setExpandedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent ref={setDialogEl} className='gap-0 p-0 sm:max-w-6xl'>
        <DialogHeader className='space-y-3 border-b px-6 pb-4 pt-6'>
          <div className='flex items-start gap-3'>
            <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Package className='h-5 w-5' />
            </span>
            <div className='min-w-0 space-y-1'>
              <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                <DialogTitle>{title ?? 'Products'}</DialogTitle>
                {patientMrn && (
                  <span className='font-mono text-sm font-medium text-muted-foreground'>MRN # {patientMrn}</span>
                )}
              </div>
              <DialogDescription>
                {description ?? (
                  <>
                    This procedure's products, or narrow to this form — reprint a label, print a
                    different one, or set where the product is held.
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
          {!loading && shown.length > 0 && (
            <div className='flex flex-wrap gap-2 text-xs'>
              <Stat label='Products' value={shown.length} />
              <Stat label='Stored' value={tally.stored} tone='sky' />
              <Stat label='Not placed' value={tally.unplaced} tone={tally.unplaced > 0 ? 'amber' : 'muted'} />
              {tally.shipped > 0 && <Stat label='Shipped' value={tally.shipped} tone='emerald' />}
            </div>
          )}
        </DialogHeader>

        <div className='flex flex-wrap items-center justify-between gap-3 px-6 py-3'>
          <div className='relative w-full max-w-sm'>
            <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder='Search DIN, product code, room or slot…'
              aria-label='Search products'
              className='h-9 pl-8'
            />
          </div>
          {scopes.length > 0 && (
            <div className='inline-flex rounded-lg bg-muted p-1 text-xs' role='group' aria-label='Which products'>
              {scopes.map(s => (
                <button
                  key={s}
                  type='button'
                  aria-pressed={scope === s}
                  onClick={() => setScope(s)}
                  className={cn(
                    'rounded-md px-3 py-1.5 font-medium transition-colors',
                    scope === s ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {PRODUCT_SCOPE_LABEL[s]}
                  {!lists[s].loading && (
                    <span className={cn('ml-1.5 tabular-nums', scope === s ? 'text-muted-foreground' : 'opacity-70')}>
                      {lists[s].rows.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className='mx-6 max-h-[58vh] overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm'>
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header || 'Actions'}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <MessageRow span={cols.visible.length}>Loading…</MessageRow>
              ) : shown.length === 0 ? (
                <MessageRow span={cols.visible.length}>
                  {scopes.length === 0
                    ? 'No products yet.'
                    : productScopeEmptyMessage(scope)}
                  {scope === 'form' && scopes.includes('procedure') && (
                    <Button variant='link' size='sm' className='ml-1 h-auto p-0' onClick={() => setScope('procedure')}>
                      Show All forms
                    </Button>
                  )}
                </MessageRow>
              ) : matching.length === 0 ? (
                <MessageRow span={cols.visible.length}>No products match your search.</MessageRow>
              ) : (
                groups.flatMap(({ product: parent, children }) => {
                  const expanded = expandedIds.includes(parent.id)
                  return [
                    <ProductRow
                      key={parent.id}
                      p={parent}
                      cols={cols}
                      childCount={children.length}
                      expanded={expanded}
                      onToggle={() => toggle(parent.id)}
                      onReprint={onReprint}
                      onChangeLabel={onChangeLabel}
                      onUpdateVolume={onUpdateVolume}
                      onRelocate={onRelocate}
                      onViewHistory={onViewHistory}
                      onViewLims={onViewLims}
                      onShip={onShip}
                      onSendToEpic={onSendToEpic}
                      {...rowProps(parent)}
                    />,
                    ...(expanded
                      ? children.map(c => (
                          <ProductRow
                            key={c.id}
                            p={c}
                            cols={cols}
                            isChild
                            onReprint={onReprint}
                            onChangeLabel={onChangeLabel}
                            onUpdateVolume={onUpdateVolume}
                            onRelocate={onRelocate}
                            onViewHistory={onViewHistory}
                            onViewLims={onViewLims}
                            onShip={onShip}
                            onSendToEpic={onSendToEpic}
                            {...rowProps(c)}
                          />
                        ))
                      : []),
                  ]
                })
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className='px-6 py-4'>
          {onOpenLabels && (
            <Button variant='outline' className='sm:mr-auto' onClick={onOpenLabels}>
              <Tags className='mr-2 h-4 w-4' />
              Labels
              {pendingLabelCount > 0 && (
                <span className='ml-2 rounded-full bg-amber-100 px-1.5 text-[11px] font-medium text-amber-800'>
                  {pendingLabelCount} to verify
                </span>
              )}
            </Button>
          )}
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      <ChangeProductCodeDialog product={changing} onClose={() => setChanging(null)} />
    </Dialog>
  )
}

const STAT_TONE = {
  default: 'border-border bg-background text-foreground',
  muted: 'border-border bg-muted/40 text-muted-foreground',
  sky: 'border-sky-200 bg-sky-50 text-sky-800',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
} as const

/** One figure in the header's summary: how many, and of what. */
function Stat({ label, value, tone = 'default' }: { label: string; value: number; tone?: keyof typeof STAT_TONE }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1', STAT_TONE[tone])}>
      <span className='font-semibold tabular-nums'>{value}</span>
      {label}
    </span>
  )
}

function MessageRow({ children, span }: { children: React.ReactNode; span: number }) {
  return (
    <TableRow>
      <TableCell colSpan={span} className='py-12 text-center text-sm text-muted-foreground'>
        {children}
      </TableCell>
    </TableRow>
  )
}

/**
 * One product row, used for both a top-level product and a split child.
 *
 * Shared rather than duplicated so a column added to the parent row cannot
 * quietly go missing on its children. A child is marked by indentation and a
 * muted DIN — it carries the SAME DIN as its parent (a split does not change
 * the donation), so the product code's division suffix is what actually tells
 * them apart, and repeating the DIN at full weight reads like a duplicate.
 */
function ProductRow({
  p,
  cols,
  isChild = false,
  childCount = 0,
  expanded = false,
  onToggle,
  onReprint,
  onChangeLabel,
  onUpdateVolume,
  onRelocate,
  onViewHistory,
  onViewLims,
  onShip,
  onSendToEpic,
  replacedBy,
  changedFrom,
  onChangeCode,
  menuContainer,
}: {
  p: CreatedProduct
  cols: ReturnType<typeof useTableColumns>
  isChild?: boolean
  childCount?: number
  expanded?: boolean
  onToggle?: () => void
  onReprint: (product: CreatedProduct) => void
  onChangeLabel: (product: CreatedProduct) => void
  onUpdateVolume: (product: CreatedProduct) => void
  onRelocate: (product: CreatedProduct) => void
  onViewHistory: (product: CreatedProduct) => void
  /** Open the read-only LIMS results for one product. */
  onViewLims: (product: CreatedProduct) => void
  onShip?: (product: CreatedProduct) => void
  onSendToEpic?: (product: CreatedProduct) => void
  /** The product that superseded this one by a code change, when it is in the list. */
  replacedBy?: CreatedProduct
  /** The product this one replaced by a code change, when it is in the list. */
  changedFrom?: CreatedProduct
  /** Omitted without products.manage, which hides the action. */
  onChangeCode?: (product: CreatedProduct) => void
  /** Where the ⋯ menu portals to: the dialog's own element. */
  menuContainer: HTMLElement | null
}) {
  const location = productLocation(p)
  const superseded = isSuperseded(p)
  // Divided by a split (z8nrz7g2wm): 0 mL, no location, its aliquots carry on.
  // Greyed like a superseded row and offered no physical action; its results
  // and its trail stay reachable.
  const usedUp = !superseded && (childCount > 0 || p.volumeMl === 0)
  const inHand = !superseded && !usedUp
  // The server refuses a split or shipped bag; offering it would be a dead end.
  const changeable = inHand && isShippable(p)
  const formLabel = associatedFormLabel(p)
  const patientLink = productPatientLink(p)
  const created = new Date(p.createdAt)

  // Everything past the two everyday actions, named. A superseded product is
  // history: its audit trail is the only thing left to do with it.
  const more: MenuAction[] = [
    ...(inHand && onShip
      ? [
          isShippable(p)
            ? { label: 'Ship this product', icon: <Truck className='h-4 w-4' />, onClick: () => onShip(p) }
            : {
                label: 'Reprint the shipping label',
                icon: <TruckFastIcon className='h-4 w-4 text-emerald-600' />,
                onClick: () => onShip(p),
              },
        ]
      : []),
    ...(inHand
      ? [
          { label: 'Print a different label', icon: <Tags className='h-4 w-4' />, onClick: () => onChangeLabel(p) },
          { label: 'Correct the recorded volume', icon: <Beaker className='h-4 w-4' />, onClick: () => onUpdateVolume(p) },
        ]
      : []),
    ...(onChangeCode && changeable
      ? [{ label: 'Change product code', icon: <ArrowLeftRight className='h-4 w-4' />, onClick: () => onChangeCode(p) }]
      : []),
    // Gated the same way ProductDetailPage gates it. Most products have no
    // results until the laboratory reports on an order, and an always-on
    // entry that opens "no lab results recorded" is a dead-end click.
    ...(hasLimsResults(p.limsValues)
      ? [{ label: 'LIMS results', icon: <FlaskConical className='h-4 w-4' />, onClick: () => onViewLims(p) }]
      : []),
    ...(!superseded && onSendToEpic
      ? [{ label: 'Send to Epic', icon: <Send className='h-4 w-4' />, onClick: () => onSendToEpic(p) }]
      : []),
    { label: 'Audit trail', icon: <History className='h-4 w-4' />, onClick: () => onViewHistory(p) },
  ]

  return (
    <TableRow className={cn('group align-top', isChild && 'bg-muted/20', (superseded || usedUp) && 'opacity-60')}>
      <ColumnCells
        columns={cols}
        cells={{
          product: (
            <TableCell className='py-3'>
              <div className='flex items-start gap-1.5' style={{ paddingLeft: isChild ? 22 : 0 }}>
                {childCount > 0 ? (
                  <button
                    type='button'
                    onClick={onToggle}
                    aria-expanded={expanded}
                    aria-label={expanded ? `Hide aliquots of ${p.din}` : `Show aliquots of ${p.din}`}
                    className='-ml-1 mt-0.5 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground'
                  >
                    {expanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
                  </button>
                ) : (
                  !isChild && <span className='w-[18px] shrink-0' />
                )}
                <div className='min-w-0'>
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <span className={cn('font-semibold tabular-nums', isChild && 'font-medium text-muted-foreground')}>
                      {p.din}
                    </span>
                    {childCount > 0 && <Pill>{childCount} split</Pill>}
                    {usedUp && (
                      <Pill title='Divided by a split: nothing is left in this container, and its aliquots carry on.'>
                        Used up
                      </Pill>
                    )}
                    {superseded && (
                      <Pill title={replacedBy ? `Changed to ${replacedBy.productDescriptionCode}` : 'Product code changed'}>
                        Superseded
                      </Pill>
                    )}
                    {!superseded && changedFrom && (
                      <Pill tone='sky' title={`Changed from ${changedFrom.productDescriptionCode}`}>
                        Changed
                      </Pill>
                    )}
                  </div>
                  <div className='mt-0.5 font-mono text-xs text-muted-foreground'>
                    <span className={cn(superseded && 'line-through')}>{p.productDescriptionCode || '—'}</span>
                    {replacedBy && <span className='ml-1'>→ {replacedBy.productDescriptionCode}</span>}
                  </div>
                </div>
              </div>
            </TableCell>
          ),
          source: (
            <TableCell className='py-3'>
              <div className='min-w-0'>
                <div className='truncate text-sm'>{formLabel ?? <span className='text-muted-foreground'>—</span>}</div>
                {/* Kit forms carry no patient, so nothing under the form is a real state. */}
                {patientLink && (
                  <Link to={patientLink.to} className='block truncate text-xs font-medium text-blue-700 hover:underline'>
                    {patientLink.label}
                  </Link>
                )}
              </div>
            </TableCell>
          ),
          volume: (
            <TableCell className='py-3 tabular-nums'>
              {p.volumeMl == null ? (
                <span className='text-muted-foreground'>—</span>
              ) : (
                <>
                  {p.volumeMl}
                  <span className='ml-0.5 text-xs text-muted-foreground'>mL</span>
                </>
              )}
            </TableCell>
          ),
          storageLocation: (
            <TableCell className='py-3'>
              {location.kind === 'shipped' ? (
                <div className='min-w-0' title={`Shipped to ${p.locationPartner?.name ?? location.text}`}>
                  <Pill tone='amber'>Shipped</Pill>
                  <div className='mt-1 truncate text-xs text-muted-foreground'>
                    to <span className='font-mono'>{location.text}</span>
                    {p.locationPartner?.name ? ` · ${p.locationPartner.name}` : ''}
                  </div>
                </div>
              ) : location.kind === 'stored' ? (
                <div className='flex min-w-0 items-start gap-1.5'>
                  <MapPin className='mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600' />
                  <LocationLines path={location.text} />
                </div>
              ) : (
                <Pill tone='amber-soft'>Not placed</Pill>
              )}
            </TableCell>
          ),
          created: (
            <TableCell className='whitespace-nowrap py-3'>
              <div className='text-sm'>{created.toLocaleDateString()}</div>
              <div className='text-xs text-muted-foreground'>
                {created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </TableCell>
          ),
          actions: (
            <TableCell className='py-2.5'>
              <div className='flex items-center justify-end gap-1'>
                {inHand && (
                  <>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-8 gap-1.5 px-2.5 text-xs'
                      onClick={() => onRelocate(p)}
                      aria-label={p.storagePath ? 'Move this product' : 'Place this product'}
                    >
                      <MapPin className='h-3.5 w-3.5' />
                      {p.storagePath ? 'Move' : 'Place'}
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-8 w-8 p-0'
                      onClick={() => onReprint(p)}
                      title='Reprint this label'
                      aria-label='Reprint this label'
                    >
                      <Printer className='h-4 w-4' />
                    </Button>
                  </>
                )}
                <RowMenu actions={more} label={`More actions for ${p.din}`} container={menuContainer} />
              </div>
            </TableCell>
          ),
        }}
      />
    </TableRow>
  )
}

const PILL_TONE = {
  muted: 'border-border bg-muted text-muted-foreground',
  sky: 'border-sky-300 bg-sky-50 text-sky-800',
  amber: 'border-amber-300 bg-amber-50 text-amber-800',
  'amber-soft': 'border-amber-200 bg-amber-50/60 text-amber-700',
} as const

function Pill({
  children,
  tone = 'muted',
  title,
}: {
  children: React.ReactNode
  tone?: keyof typeof PILL_TONE
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        PILL_TONE[tone],
      )}
    >
      {children}
    </span>
  )
}

interface MenuAction {
  label: string
  icon: React.ReactNode
  onClick: () => void
}

/**
 * A row's less frequent actions, each named. The menu closes before the
 * action runs, since most open a dialog of their own.
 */
function RowMenu({ actions, label, container }: { actions: MenuAction[]; label: string; container: HTMLElement | null }) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='ghost' size='sm' className='h-8 w-8 p-0' title='More actions' aria-label={label}>
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' container={container} className='w-56 p-1'>
        <div role='menu' aria-label={label}>
          {actions.map(a => (
            <button
              key={a.label}
              type='button'
              role='menuitem'
              aria-label={a.label}
              onClick={() => {
                setOpen(false)
                a.onClick()
              }}
              className='flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none'
            >
              <span className='text-muted-foreground'>{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
